import {
  DocumentSnapshot,
  QueryDocumentSnapshot,
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  orderBy,
  limit,
  startAfter,
  writeBatch,
  increment,
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import { Collections } from "@/helpers/utils/collections";
import {
  Workspace,
  Space,
  List,
  Task,
  TaskComment,
  TaskAttachment,
  TaskActivity,
  TaskAssignee,
  WorkspaceMember,
  KanbanStatus,
  CreateTaskRequest,
  UpdateTaskRequest,
  MoveTaskRequest,
  TaskQueryFilters,
  PaginatedTasksRequest,
  PaginatedTasksResponse,
  getDefaultWorkspaceSettings,
  generateSlug,
  DEFAULT_STATUSES,
  TaskPriority,
  TaskActivityType,
  WorkspaceInvite,
} from "@/models/kanban";
import {
  KeyResult,
  calculateKeyResultProgress,
  calculateGoalProgress,
  getGoalStatusFromProgress,
} from "@/models/okr";

// ============================================================================
// Document Mappers
// ============================================================================

const workspaceMap = (doc: DocumentSnapshot): Workspace =>
  ({
    id: doc.id,
    ...doc.data(),
  } as Workspace);

const spaceMap = (doc: DocumentSnapshot): Space =>
  ({
    id: doc.id,
    ...doc.data(),
  } as Space);

const listMap = (doc: DocumentSnapshot): List =>
  ({
    id: doc.id,
    ...doc.data(),
  } as List);

const taskMap = (doc: DocumentSnapshot): Task =>
  ({
    id: doc.id,
    ...doc.data(),
  } as Task);

const commentMap = (doc: DocumentSnapshot): TaskComment =>
  ({
    id: doc.id,
    ...doc.data(),
  } as TaskComment);

const activityMap = (doc: DocumentSnapshot): TaskActivity =>
  ({
    id: doc.id,
    ...doc.data(),
  } as TaskActivity);

// ============================================================================
// Workspace Operations
// ============================================================================

export async function getWorkspaces(): Promise<Workspace[]> {
  const workspacesRef = collection(db, Collections.KANBAN.WORKSPACES);
  const snapshot = await getDocs(query(workspacesRef, orderBy("createdAt", "desc")));
  return snapshot.docs.map(workspaceMap);
}

export async function getWorkspacesByUser(userId: string): Promise<Workspace[]> {
  const workspacesRef = collection(db, Collections.KANBAN.WORKSPACES);
  const q = query(workspacesRef, where("member_ids", "array-contains", userId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(workspaceMap);
}

export async function getWorkspaceById(workspaceId: string): Promise<Workspace | null> {
  const workspaceRef = doc(db, Collections.KANBAN.WORKSPACES, workspaceId);
  const snapshot = await getDoc(workspaceRef);
  return snapshot.exists() ? workspaceMap(snapshot) : null;
}

export async function getWorkspaceBySlug(slug: string): Promise<Workspace | null> {
  const workspacesRef = collection(db, Collections.KANBAN.WORKSPACES);
  const q = query(workspacesRef, where("slug", "==", slug), limit(1));
  const snapshot = await getDocs(q);
  return snapshot.empty ? null : workspaceMap(snapshot.docs[0]);
}

export async function createWorkspace(
  data: {
    name: string;
    description?: string;
    type: "internal" | "partner";
    iconUrl?: string;
    color?: string;
  },
  creatorId: string,
  creatorEmail: string,
  creatorDisplayName: string
): Promise<Workspace> {
  const workspacesRef = collection(db, Collections.KANBAN.WORKSPACES);

  const ownerMember: WorkspaceMember = {
    userId: creatorId,
    email: creatorEmail,
    displayName: creatorDisplayName,
    role: "owner",
    addedAt: Timestamp.now(),
    addedBy: creatorId,
  };

  const workspaceData = {
    name: data.name,
    slug: generateSlug(data.name),
    description: data.description || null,
    type: data.type,
    iconUrl: data.iconUrl || null,
    color: data.color || "#3b82f6",
    members: [ownerMember],
    // Denormalized array for Firestore security rules
    member_ids: [creatorId],
    owner_id: creatorId,
    settings: getDefaultWorkspaceSettings(),
    createdAt: serverTimestamp(),
    createdBy: creatorId,
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(workspacesRef, workspaceData);
  const newDoc = await getDoc(docRef);
  return workspaceMap(newDoc);
}

export async function updateWorkspace(
  workspaceId: string,
  data: Partial<Pick<Workspace, "name" | "description" | "iconUrl" | "color" | "settings">>
): Promise<Workspace | null> {
  const workspaceRef = doc(db, Collections.KANBAN.WORKSPACES, workspaceId);

  const updateData: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (data.name !== undefined) {
    updateData.name = data.name;
    updateData.slug = generateSlug(data.name);
  }
  if (data.description !== undefined) updateData.description = data.description;
  if (data.iconUrl !== undefined) updateData.iconUrl = data.iconUrl;
  if (data.color !== undefined) updateData.color = data.color;
  if (data.settings !== undefined) updateData.settings = data.settings;

  await updateDoc(workspaceRef, updateData);
  const updatedDoc = await getDoc(workspaceRef);
  return updatedDoc.exists() ? workspaceMap(updatedDoc) : null;
}

export async function addWorkspaceMember(
  workspaceId: string,
  member: WorkspaceMember
): Promise<Workspace | null> {
  const workspaceRef = doc(db, Collections.KANBAN.WORKSPACES, workspaceId);
  const workspace = await getWorkspaceById(workspaceId);
  if (!workspace) return null;

  const updatedMembers = [...workspace.members, member];
  const updatedMemberIds = [...(workspace.member_ids || []), member.userId];
  await updateDoc(workspaceRef, {
    members: updatedMembers,
    member_ids: updatedMemberIds,
    updatedAt: serverTimestamp(),
  });

  const updatedDoc = await getDoc(workspaceRef);
  return workspaceMap(updatedDoc);
}

export async function createWorkspaceInvite(
  workspaceId: string,
  invite: Omit<WorkspaceInvite, "id" | "status" | "createdAt">
): Promise<string> {
  const invitesRef = collection(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.INVITES
  );
  const docRef = await addDoc(invitesRef, {
    ...invite,
    status: "pending",
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function getWorkspaceInvite(
  workspaceId: string,
  inviteId: string
): Promise<WorkspaceInvite | null> {
  const inviteRef = doc(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.INVITES,
    inviteId
  );
  const snapshot = await getDoc(inviteRef);
  return snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as WorkspaceInvite) : null;
}

export async function acceptWorkspaceInvite(
  workspaceId: string,
  inviteId: string,
  user: { uid: string; email: string; displayName?: string }
): Promise<void> {
  const inviteRef = doc(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.INVITES,
    inviteId
  );
  await updateDoc(inviteRef, {
    status: "accepted",
    acceptedById: user.uid,
    acceptedByEmail: user.email,
    acceptedByName: user.displayName || user.email,
    acceptedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function declineWorkspaceInvite(
  workspaceId: string,
  inviteId: string,
  user: { uid: string; email: string; displayName?: string }
): Promise<void> {
  const inviteRef = doc(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.INVITES,
    inviteId
  );
  await updateDoc(inviteRef, {
    status: "declined",
    acceptedById: user.uid,
    acceptedByEmail: user.email,
    acceptedByName: user.displayName || user.email,
    declinedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function removeWorkspaceMember(
  workspaceId: string,
  userId: string
): Promise<Workspace | null> {
  const workspaceRef = doc(db, Collections.KANBAN.WORKSPACES, workspaceId);
  const workspace = await getWorkspaceById(workspaceId);
  if (!workspace) return null;

  const updatedMembers = workspace.members.filter((m) => m.userId !== userId);
  const updatedMemberIds = (workspace.member_ids || []).filter((id) => id !== userId);
  await updateDoc(workspaceRef, {
    members: updatedMembers,
    member_ids: updatedMemberIds,
    updatedAt: serverTimestamp(),
  });

  const updatedDoc = await getDoc(workspaceRef);
  return workspaceMap(updatedDoc);
}

export async function deleteWorkspace(workspaceId: string): Promise<boolean> {
  try {
    // Delete all tasks in the workspace first
    const tasksRef = collection(db, Collections.KANBAN.TASKS);
    const tasksQuery = query(tasksRef, where("workspaceId", "==", workspaceId));
    const tasksSnapshot = await getDocs(tasksQuery);

    const batch = writeBatch(db);
    tasksSnapshot.docs.forEach((taskDoc) => {
      batch.delete(taskDoc.ref);
    });

    // Delete the workspace
    const workspaceRef = doc(db, Collections.KANBAN.WORKSPACES, workspaceId);
    batch.delete(workspaceRef);

    await batch.commit();
    return true;
  } catch (error) {
    console.error("Error deleting workspace:", error);
    return false;
  }
}

// ============================================================================
// Space Operations
// ============================================================================

export async function getSpaces(workspaceId: string): Promise<Space[]> {
  const spacesRef = collection(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.SPACES
  );
  const snapshot = await getDocs(query(spacesRef, orderBy("order", "asc")));
  return snapshot.docs.map(spaceMap);
}

export async function getSpaceById(workspaceId: string, spaceId: string): Promise<Space | null> {
  const spaceRef = doc(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.SPACES,
    spaceId
  );
  const snapshot = await getDoc(spaceRef);
  return snapshot.exists() ? spaceMap(snapshot) : null;
}

export async function getSpaceBySlug(
  workspaceId: string,
  slug: string
): Promise<Space | null> {
  const spacesRef = collection(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.SPACES
  );
  const q = query(spacesRef, where("slug", "==", slug), limit(1));
  const snapshot = await getDocs(q);
  return snapshot.empty ? null : spaceMap(snapshot.docs[0]);
}

export async function createSpace(
  workspaceId: string,
  data: {
    name: string;
    description?: string;
    iconUrl?: string;
    color?: string;
  },
  creatorId: string
): Promise<Space> {
  const spacesRef = collection(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.SPACES
  );

  // Get current max order
  const existingSpaces = await getSpaces(workspaceId);
  const maxOrder = existingSpaces.reduce((max, s) => Math.max(max, s.order), -1);

  const spaceData = {
    workspaceId,
    name: data.name,
    slug: generateSlug(data.name),
    description: data.description || null,
    iconUrl: data.iconUrl || null,
    color: data.color || "#6366f1",
    order: maxOrder + 1,
    createdAt: serverTimestamp(),
    createdBy: creatorId,
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(spacesRef, spaceData);
  const newDoc = await getDoc(docRef);
  return spaceMap(newDoc);
}

export async function updateSpace(
  workspaceId: string,
  spaceId: string,
  data: Partial<Pick<Space, "name" | "description" | "iconUrl" | "color" | "order">>
): Promise<Space | null> {
  const spaceRef = doc(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.SPACES,
    spaceId
  );

  const updateData: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (data.name !== undefined) {
    updateData.name = data.name;
    updateData.slug = generateSlug(data.name);
  }
  if (data.description !== undefined) updateData.description = data.description;
  if (data.iconUrl !== undefined) updateData.iconUrl = data.iconUrl;
  if (data.color !== undefined) updateData.color = data.color;
  if (data.order !== undefined) updateData.order = data.order;

  await updateDoc(spaceRef, updateData);
  const updatedDoc = await getDoc(spaceRef);
  return updatedDoc.exists() ? spaceMap(updatedDoc) : null;
}

export async function deleteSpace(workspaceId: string, spaceId: string): Promise<boolean> {
  try {
    // Delete all tasks in the space first
    const tasksRef = collection(db, Collections.KANBAN.TASKS);
    const tasksQuery = query(tasksRef, where("spaceId", "==", spaceId));
    const tasksSnapshot = await getDocs(tasksQuery);

    const batch = writeBatch(db);
    tasksSnapshot.docs.forEach((taskDoc) => {
      batch.delete(taskDoc.ref);
    });

    // Delete the space
    const spaceRef = doc(
      db,
      Collections.KANBAN.WORKSPACES,
      workspaceId,
      Collections.KANBAN.SPACES,
      spaceId
    );
    batch.delete(spaceRef);

    await batch.commit();
    return true;
  } catch (error) {
    console.error("Error deleting space:", error);
    return false;
  }
}

// ============================================================================
// List Operations
// ============================================================================

export async function getLists(workspaceId: string, spaceId: string): Promise<List[]> {
  const listsRef = collection(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.SPACES,
    spaceId,
    Collections.KANBAN.LISTS
  );
  const snapshot = await getDocs(query(listsRef, orderBy("order", "asc")));
  return snapshot.docs.map(listMap);
}

export async function getListById(
  workspaceId: string,
  spaceId: string,
  listId: string
): Promise<List | null> {
  const listRef = doc(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.SPACES,
    spaceId,
    Collections.KANBAN.LISTS,
    listId
  );
  const snapshot = await getDoc(listRef);
  return snapshot.exists() ? listMap(snapshot) : null;
}

export async function getListBySlug(
  workspaceId: string,
  spaceId: string,
  slug: string
): Promise<List | null> {
  const listsRef = collection(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.SPACES,
    spaceId,
    Collections.KANBAN.LISTS
  );
  const q = query(listsRef, where("slug", "==", slug), limit(1));
  const snapshot = await getDocs(q);
  return snapshot.empty ? null : listMap(snapshot.docs[0]);
}

export async function createList(
  workspaceId: string,
  spaceId: string,
  data: {
    name: string;
    description?: string;
    statuses?: KanbanStatus[];
  },
  creatorId: string
): Promise<List> {
  const listsRef = collection(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.SPACES,
    spaceId,
    Collections.KANBAN.LISTS
  );

  // Get current max order
  const existingLists = await getLists(workspaceId, spaceId);
  const maxOrder = existingLists.reduce((max, l) => Math.max(max, l.order), -1);

  const listData = {
    workspaceId,
    spaceId,
    name: data.name,
    slug: generateSlug(data.name),
    description: data.description || null,
    statuses: data.statuses || [...DEFAULT_STATUSES],
    order: maxOrder + 1,
    taskCount: 0,
    createdAt: serverTimestamp(),
    createdBy: creatorId,
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(listsRef, listData);
  const newDoc = await getDoc(docRef);
  return listMap(newDoc);
}

export async function updateList(
  workspaceId: string,
  spaceId: string,
  listId: string,
  data: Partial<Pick<List, "name" | "description" | "statuses" | "order">>
): Promise<List | null> {
  const listRef = doc(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.SPACES,
    spaceId,
    Collections.KANBAN.LISTS,
    listId
  );

  const updateData: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (data.name !== undefined) {
    updateData.name = data.name;
    updateData.slug = generateSlug(data.name);
  }
  if (data.description !== undefined) updateData.description = data.description;
  if (data.statuses !== undefined) updateData.statuses = data.statuses;
  if (data.order !== undefined) updateData.order = data.order;

  await updateDoc(listRef, updateData);
  const updatedDoc = await getDoc(listRef);
  return updatedDoc.exists() ? listMap(updatedDoc) : null;
}

export async function deleteList(
  workspaceId: string,
  spaceId: string,
  listId: string
): Promise<boolean> {
  try {
    // Delete all tasks in the list first
    const tasksRef = collection(db, Collections.KANBAN.TASKS);
    const tasksQuery = query(tasksRef, where("listId", "==", listId));
    const tasksSnapshot = await getDocs(tasksQuery);

    const batch = writeBatch(db);
    tasksSnapshot.docs.forEach((taskDoc) => {
      batch.delete(taskDoc.ref);
    });

    // Delete the list
    const listRef = doc(
      db,
      Collections.KANBAN.WORKSPACES,
      workspaceId,
      Collections.KANBAN.SPACES,
      spaceId,
      Collections.KANBAN.LISTS,
      listId
    );
    batch.delete(listRef);

    await batch.commit();
    return true;
  } catch (error) {
    console.error("Error deleting list:", error);
    return false;
  }
}

// ============================================================================
// Task Operations
// ============================================================================

export async function getTasksByList(listId: string): Promise<Task[]> {
  const tasksRef = collection(db, Collections.KANBAN.TASKS);
  const q = query(
    tasksRef,
    where("listId", "==", listId),
    where("parentTaskId", "==", null),
    orderBy("orderInStatus", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(taskMap);
}

export async function getTasksByStatus(listId: string, statusId: string): Promise<Task[]> {
  const tasksRef = collection(db, Collections.KANBAN.TASKS);
  const q = query(
    tasksRef,
    where("listId", "==", listId),
    where("statusId", "==", statusId),
    where("parentTaskId", "==", null),
    orderBy("orderInStatus", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(taskMap);
}

export async function getTaskById(taskId: string): Promise<Task | null> {
  const taskRef = doc(db, Collections.KANBAN.TASKS, taskId);
  const snapshot = await getDoc(taskRef);
  return snapshot.exists() ? taskMap(snapshot) : null;
}

export async function getSubtasks(parentTaskId: string): Promise<Task[]> {
  const tasksRef = collection(db, Collections.KANBAN.TASKS);
  const q = query(
    tasksRef,
    where("parentTaskId", "==", parentTaskId),
    orderBy("orderInStatus", "asc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(taskMap);
}

export async function queryTasks(request: PaginatedTasksRequest): Promise<PaginatedTasksResponse> {
  const tasksRef = collection(db, Collections.KANBAN.TASKS);
  const whereConstraints: ReturnType<typeof where>[] = [];

  const { filters } = request;

  if (filters.workspaceId) {
    whereConstraints.push(where("workspaceId", "==", filters.workspaceId));
  }
  if (filters.spaceId) {
    whereConstraints.push(where("spaceId", "==", filters.spaceId));
  }
  if (filters.listId) {
    whereConstraints.push(where("listId", "==", filters.listId));
  }
  if (filters.statusId) {
    whereConstraints.push(where("statusId", "==", filters.statusId));
  }
  if (filters.priority) {
    whereConstraints.push(where("priority", "==", filters.priority));
  }
  if (filters.assigneeId) {
    whereConstraints.push(where("assignees", "array-contains", { userId: filters.assigneeId }));
  }
  if (filters.createdBy) {
    whereConstraints.push(where("createdBy", "==", filters.createdBy));
  }
  if (filters.parentTaskId === null) {
    whereConstraints.push(where("parentTaskId", "==", null));
  } else if (filters.parentTaskId) {
    whereConstraints.push(where("parentTaskId", "==", filters.parentTaskId));
  }

  const orderByField = request.orderBy || "createdAt";
  const orderDirection = request.orderDirection || "desc";
  const orderByConstraint = orderBy(orderByField, orderDirection);

  const queryLimit = request.limit || 50;
  const limitConstraint = limit(queryLimit + 1); // +1 to check if there are more

  let q;
  if (request.cursor) {
    const cursorDoc = await getDoc(doc(db, Collections.KANBAN.TASKS, request.cursor));
    if (cursorDoc.exists()) {
      q = query(tasksRef, ...whereConstraints, orderByConstraint, startAfter(cursorDoc), limitConstraint);
    } else {
      q = query(tasksRef, ...whereConstraints, orderByConstraint, limitConstraint);
    }
  } else {
    q = query(tasksRef, ...whereConstraints, orderByConstraint, limitConstraint);
  }
  const snapshot = await getDocs(q);
  const tasks = snapshot.docs.map(taskMap);

  const hasMore = tasks.length > queryLimit;
  if (hasMore) {
    tasks.pop(); // Remove the extra item
  }

  return {
    tasks,
    hasMore,
    nextCursor: hasMore && tasks.length > 0 ? tasks[tasks.length - 1].id : undefined,
  };
}

export async function createTask(
  request: CreateTaskRequest,
  creatorId: string
): Promise<Task> {
  const tasksRef = collection(db, Collections.KANBAN.TASKS);

  // Get max order for the status
  const existingTasks = await getTasksByStatus(
    request.listId,
    request.statusId || "todo"
  );
  const maxOrder = existingTasks.reduce((max, t) => Math.max(max, t.orderInStatus), -1);

  const taskData = {
    workspaceId: request.workspaceId,
    spaceId: request.spaceId,
    listId: request.listId,
    title: request.title,
    description: request.description || null,
    statusId: request.statusId || "todo",
    priority: request.priority || "normal",
    assignees: [], // Will be populated separately if assigneeIds provided
    watchers: [creatorId],
    dueDate: request.dueDate ? Timestamp.fromDate(new Date(request.dueDate)) : null,
    startDate: null,
    tags: request.tags || [],
    customFields: {},
    parentTaskId: request.parentTaskId || null,
    goalId: request.goalId || null,
    keyResultId: request.keyResultId || null,
    subtaskCount: 0,
    subtaskCompletedCount: 0,
    commentCount: 0,
    attachmentCount: 0,
    orderInStatus: maxOrder + 1,
    createdAt: serverTimestamp(),
    createdBy: creatorId,
    updatedAt: serverTimestamp(),
    completedAt: null,
    completedBy: null,
  };

  const docRef = await addDoc(tasksRef, taskData);

  // Update list task count
  await updateListTaskCount(request.workspaceId, request.spaceId, request.listId, 1);

  // Update parent subtask count if this is a subtask
  if (request.parentTaskId) {
    await updateParentSubtaskCount(request.parentTaskId, 1);
  }

  // Link task to Key Result if specified
  if (request.keyResultId && request.goalId) {
    await linkTaskToKeyResult(
      request.workspaceId,
      request.goalId,
      request.keyResultId,
      docRef.id
    );
  }

  // Log activity
  await addTaskActivity(docRef.id, "created", creatorId, "User");

  const newDoc = await getDoc(docRef);
  return taskMap(newDoc);
}

export async function updateTask(
  request: UpdateTaskRequest,
  updaterId: string,
  updaterDisplayName: string
): Promise<Task | null> {
  const taskRef = doc(db, Collections.KANBAN.TASKS, request.taskId);
  const existingTask = await getTaskById(request.taskId);
  if (!existingTask) return null;

  const updateData: Record<string, unknown> = { updatedAt: serverTimestamp() };
  const activities: { type: TaskActivityType; prev?: string; next?: string }[] = [];

  if (request.title !== undefined && request.title !== existingTask.title) {
    updateData.title = request.title;
    activities.push({ type: "title_changed", prev: existingTask.title, next: request.title });
  }
  if (request.description !== undefined) {
    updateData.description = request.description;
    if (request.description !== existingTask.description) {
      activities.push({ type: "description_changed" });
    }
  }
  if (request.priority !== undefined && request.priority !== existingTask.priority) {
    updateData.priority = request.priority;
    activities.push({
      type: "priority_changed",
      prev: existingTask.priority,
      next: request.priority,
    });
  }
  if (request.statusId !== undefined && request.statusId !== existingTask.statusId) {
    updateData.statusId = request.statusId;
    activities.push({
      type: "status_changed",
      prev: existingTask.statusId,
      next: request.statusId,
    });

    // Handle completion
    if (request.statusId === "done" && existingTask.statusId !== "done") {
      updateData.completedAt = serverTimestamp();
      updateData.completedBy = updaterId;

      // Update parent subtask completed count
      if (existingTask.parentTaskId) {
        await updateParentSubtaskCompletedCount(existingTask.parentTaskId, 1);
      }
    } else if (request.statusId !== "done" && existingTask.statusId === "done") {
      updateData.completedAt = null;
      updateData.completedBy = null;

      // Decrement parent subtask completed count
      if (existingTask.parentTaskId) {
        await updateParentSubtaskCompletedCount(existingTask.parentTaskId, -1);
      }
    }
  }
  if (request.dueDate !== undefined) {
    updateData.dueDate = request.dueDate
      ? Timestamp.fromDate(new Date(request.dueDate))
      : null;
    activities.push({ type: "due_date_changed" });
  }
  if (request.tags !== undefined) {
    updateData.tags = request.tags;
  }
  if (request.customFields !== undefined) {
    updateData.customFields = request.customFields;
  }

  // Handle goal link/unlink
  if (request.goalId !== undefined || request.keyResultId !== undefined) {
    const oldGoalId = existingTask.goalId;
    const oldKeyResultId = existingTask.keyResultId;
    const newGoalId = request.goalId === null ? null : (request.goalId ?? oldGoalId);
    const newKeyResultId = request.keyResultId === null ? null : (request.keyResultId ?? oldKeyResultId);

    // Unlink from old KR if changing
    if (oldKeyResultId && oldGoalId && oldKeyResultId !== newKeyResultId) {
      await unlinkTaskFromKeyResult(
        existingTask.workspaceId,
        oldGoalId,
        oldKeyResultId,
        request.taskId
      );
      // If old task was done, decrement old KR
      if (existingTask.statusId === "done") {
        await handleKRAutoProgress(existingTask.workspaceId, oldGoalId, oldKeyResultId, -1);
      }
    }

    // Link to new KR if setting
    if (newKeyResultId && newGoalId && newKeyResultId !== oldKeyResultId) {
      await linkTaskToKeyResult(
        existingTask.workspaceId,
        newGoalId,
        newKeyResultId,
        request.taskId
      );
      // If task is already done, increment new KR
      if (existingTask.statusId === "done" && !request.statusId) {
        await handleKRAutoProgress(existingTask.workspaceId, newGoalId, newKeyResultId, 1);
      }
    }

    updateData.goalId = newGoalId;
    updateData.keyResultId = newKeyResultId;

    if (newGoalId && !oldGoalId) {
      activities.push({ type: "goal_linked", next: newGoalId });
    } else if (!newGoalId && oldGoalId) {
      activities.push({ type: "goal_unlinked", prev: oldGoalId });
    }
  }

  // Handle KR auto-progress on status change (for already-linked tasks)
  if (request.statusId !== undefined && request.statusId !== existingTask.statusId) {
    const effectiveGoalId = (updateData.goalId as string | null | undefined) ?? existingTask.goalId;
    const effectiveKrId = (updateData.keyResultId as string | null | undefined) ?? existingTask.keyResultId;

    if (effectiveKrId && effectiveGoalId) {
      if (request.statusId === "done" && existingTask.statusId !== "done") {
        await handleKRAutoProgress(existingTask.workspaceId, effectiveGoalId, effectiveKrId, 1);
      } else if (request.statusId !== "done" && existingTask.statusId === "done") {
        await handleKRAutoProgress(existingTask.workspaceId, effectiveGoalId, effectiveKrId, -1);
      }
    }
  }

  await updateDoc(taskRef, updateData);

  // Log activities
  for (const activity of activities) {
    await addTaskActivity(
      request.taskId,
      activity.type,
      updaterId,
      updaterDisplayName,
      activity.prev,
      activity.next
    );
  }

  const updatedDoc = await getDoc(taskRef);
  return updatedDoc.exists() ? taskMap(updatedDoc) : null;
}

export async function moveTask(request: MoveTaskRequest, userId: string): Promise<Task | null> {
  const taskRef = doc(db, Collections.KANBAN.TASKS, request.taskId);
  const existingTask = await getTaskById(request.taskId);
  if (!existingTask) return null;

  const updateData: Record<string, unknown> = {
    statusId: request.targetStatusId,
    orderInStatus: request.targetOrder,
    updatedAt: serverTimestamp(),
  };

  // Handle list change
  if (request.targetListId && request.targetListId !== existingTask.listId) {
    updateData.listId = request.targetListId;

    // Update task counts
    await updateListTaskCount(
      existingTask.workspaceId,
      existingTask.spaceId,
      existingTask.listId,
      -1
    );
    // Note: We need the space ID for the new list - simplified for now
    await addTaskActivity(request.taskId, "moved", userId, "User");
  }

  // Handle completion status
  if (request.targetStatusId === "done" && existingTask.statusId !== "done") {
    updateData.completedAt = serverTimestamp();
    updateData.completedBy = userId;
  } else if (request.targetStatusId !== "done" && existingTask.statusId === "done") {
    updateData.completedAt = null;
    updateData.completedBy = null;
  }

  // Handle KR auto-progress on status change
  if (request.targetStatusId !== existingTask.statusId && existingTask.keyResultId && existingTask.goalId) {
    if (request.targetStatusId === "done" && existingTask.statusId !== "done") {
      await handleKRAutoProgress(existingTask.workspaceId, existingTask.goalId, existingTask.keyResultId, 1);
    } else if (request.targetStatusId !== "done" && existingTask.statusId === "done") {
      await handleKRAutoProgress(existingTask.workspaceId, existingTask.goalId, existingTask.keyResultId, -1);
    }
  }

  await updateDoc(taskRef, updateData);

  if (request.targetStatusId !== existingTask.statusId) {
    await addTaskActivity(
      request.taskId,
      "status_changed",
      userId,
      "User",
      existingTask.statusId,
      request.targetStatusId
    );
  }

  const updatedDoc = await getDoc(taskRef);
  return updatedDoc.exists() ? taskMap(updatedDoc) : null;
}

export async function deleteTask(taskId: string): Promise<boolean> {
  try {
    const task = await getTaskById(taskId);
    if (!task) return false;

    const batch = writeBatch(db);

    // Delete subtasks
    const subtasks = await getSubtasks(taskId);
    for (const subtask of subtasks) {
      batch.delete(doc(db, Collections.KANBAN.TASKS, subtask.id));
    }

    // Delete the task
    batch.delete(doc(db, Collections.KANBAN.TASKS, taskId));

    await batch.commit();

    // Update list task count
    await updateListTaskCount(task.workspaceId, task.spaceId, task.listId, -(1 + subtasks.length));

    // Update parent subtask count if this was a subtask
    if (task.parentTaskId) {
      await updateParentSubtaskCount(task.parentTaskId, -1);
      if (task.statusId === "done") {
        await updateParentSubtaskCompletedCount(task.parentTaskId, -1);
      }
    }

    // Clean up goal/KR links
    if (task.keyResultId && task.goalId) {
      await unlinkTaskFromKeyResult(task.workspaceId, task.goalId, task.keyResultId, taskId);
      // Decrement KR if task was done
      if (task.statusId === "done") {
        await handleKRAutoProgress(task.workspaceId, task.goalId, task.keyResultId, -1);
      }
    }

    return true;
  } catch (error) {
    console.error("Error deleting task:", error);
    return false;
  }
}

export async function addTaskAssignee(
  taskId: string,
  assignee: TaskAssignee,
  assignerId: string,
  assignerDisplayName: string
): Promise<Task | null> {
  const task = await getTaskById(taskId);
  if (!task) return null;

  // Check if already assigned
  if (task.assignees.some((a) => a.userId === assignee.userId)) {
    return task;
  }

  const taskRef = doc(db, Collections.KANBAN.TASKS, taskId);
  const updatedAssignees = [...task.assignees, assignee];

  await updateDoc(taskRef, {
    assignees: updatedAssignees,
    updatedAt: serverTimestamp(),
  });

  await addTaskActivity(
    taskId,
    "assignee_added",
    assignerId,
    assignerDisplayName,
    undefined,
    assignee.displayName
  );

  const updatedDoc = await getDoc(taskRef);
  return taskMap(updatedDoc);
}

export async function removeTaskAssignee(
  taskId: string,
  userId: string,
  removerId: string,
  removerDisplayName: string
): Promise<Task | null> {
  const task = await getTaskById(taskId);
  if (!task) return null;

  const removedAssignee = task.assignees.find((a) => a.userId === userId);
  if (!removedAssignee) return task;

  const taskRef = doc(db, Collections.KANBAN.TASKS, taskId);
  const updatedAssignees = task.assignees.filter((a) => a.userId !== userId);

  await updateDoc(taskRef, {
    assignees: updatedAssignees,
    updatedAt: serverTimestamp(),
  });

  await addTaskActivity(
    taskId,
    "assignee_removed",
    removerId,
    removerDisplayName,
    removedAssignee.displayName,
    undefined
  );

  const updatedDoc = await getDoc(taskRef);
  return taskMap(updatedDoc);
}

// ============================================================================
// Comment Operations
// ============================================================================

export async function getTaskComments(taskId: string): Promise<TaskComment[]> {
  const commentsRef = collection(
    db,
    Collections.KANBAN.TASKS,
    taskId,
    Collections.KANBAN.COMMENTS
  );
  const snapshot = await getDocs(query(commentsRef, orderBy("createdAt", "asc")));
  return snapshot.docs.map(commentMap);
}

export async function addTaskComment(
  taskId: string,
  content: string,
  creatorId: string,
  mentions: TaskComment["mentions"] = [],
  creatorDisplayName: string = "User"
): Promise<TaskComment> {
  const commentsRef = collection(
    db,
    Collections.KANBAN.TASKS,
    taskId,
    Collections.KANBAN.COMMENTS
  );

  const commentData = {
    taskId,
    content,
    mentions,
    createdAt: serverTimestamp(),
    createdBy: creatorId,
    creatorDisplayName,
  };

  const docRef = await addDoc(commentsRef, commentData);

  // Update task comment count
  const taskRef = doc(db, Collections.KANBAN.TASKS, taskId);
  await updateDoc(taskRef, {
    commentCount: increment(1),
    updatedAt: serverTimestamp(),
  });

  await addTaskActivity(taskId, "comment_added", creatorId, creatorDisplayName);

  const newDoc = await getDoc(docRef);
  return commentMap(newDoc);
}

// ============================================================================
// Attachment Operations
// ============================================================================

export async function getTaskAttachments(taskId: string): Promise<TaskAttachment[]> {
  const attachmentsRef = collection(
    db,
    Collections.KANBAN.TASKS,
    taskId,
    Collections.KANBAN.ATTACHMENTS
  );
  const snapshot = await getDocs(query(attachmentsRef, orderBy("createdAt", "asc")));
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as TaskAttachment));
}

export async function addTaskAttachment(
  taskId: string,
  attachment: Omit<TaskAttachment, "id" | "taskId" | "createdAt" | "createdBy">,
  creatorId: string
): Promise<TaskAttachment> {
  const attachmentsRef = collection(
    db,
    Collections.KANBAN.TASKS,
    taskId,
    Collections.KANBAN.ATTACHMENTS
  );

  const attachmentData = {
    taskId,
    ...attachment,
    createdAt: serverTimestamp(),
    createdBy: creatorId,
  };

  const docRef = await addDoc(attachmentsRef, attachmentData);

  const taskRef = doc(db, Collections.KANBAN.TASKS, taskId);
  await updateDoc(taskRef, {
    attachmentCount: increment(1),
    updatedAt: serverTimestamp(),
  });

  const newDoc = await getDoc(docRef);
  return { id: newDoc.id, ...newDoc.data() } as TaskAttachment;
}

export async function deleteTaskAttachment(
  taskId: string,
  attachmentId: string
): Promise<boolean> {
  try {
    const attachmentRef = doc(
      db,
      Collections.KANBAN.TASKS,
      taskId,
      Collections.KANBAN.ATTACHMENTS,
      attachmentId
    );
    await deleteDoc(attachmentRef);

    const taskRef = doc(db, Collections.KANBAN.TASKS, taskId);
    await updateDoc(taskRef, {
      attachmentCount: increment(-1),
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error("Error deleting attachment:", error);
    return false;
  }
}

export async function updateTaskComment(
  taskId: string,
  commentId: string,
  content: string
): Promise<TaskComment | null> {
  const commentRef = doc(
    db,
    Collections.KANBAN.TASKS,
    taskId,
    Collections.KANBAN.COMMENTS,
    commentId
  );

  await updateDoc(commentRef, {
    content,
    updatedAt: serverTimestamp(),
    editedAt: serverTimestamp(),
  });

  const updatedDoc = await getDoc(commentRef);
  return updatedDoc.exists() ? commentMap(updatedDoc) : null;
}

export async function deleteTaskComment(taskId: string, commentId: string): Promise<boolean> {
  try {
    const commentRef = doc(
      db,
      Collections.KANBAN.TASKS,
      taskId,
      Collections.KANBAN.COMMENTS,
      commentId
    );
    await deleteDoc(commentRef);

    // Update task comment count
    const taskRef = doc(db, Collections.KANBAN.TASKS, taskId);
    await updateDoc(taskRef, {
      commentCount: increment(-1),
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error("Error deleting comment:", error);
    return false;
  }
}

// ============================================================================
// Activity Log Operations
// ============================================================================

export async function getTaskActivity(
  taskId: string,
  limitCount = 50
): Promise<TaskActivity[]> {
  const activityRef = collection(
    db,
    Collections.KANBAN.TASKS,
    taskId,
    Collections.KANBAN.ACTIVITY
  );
  const snapshot = await getDocs(
    query(activityRef, orderBy("createdAt", "desc"), limit(limitCount))
  );
  return snapshot.docs.map(activityMap);
}

async function addTaskActivity(
  taskId: string,
  type: TaskActivityType,
  userId: string,
  userDisplayName: string,
  previousValue?: string,
  newValue?: string
): Promise<void> {
  const activityRef = collection(
    db,
    Collections.KANBAN.TASKS,
    taskId,
    Collections.KANBAN.ACTIVITY
  );

  await addDoc(activityRef, {
    taskId,
    type,
    userId,
    userDisplayName,
    previousValue: previousValue || null,
    newValue: newValue || null,
    createdAt: serverTimestamp(),
  });
}

// ============================================================================
// Helper Functions
// ============================================================================

async function updateListTaskCount(
  workspaceId: string,
  spaceId: string,
  listId: string,
  delta: number
): Promise<void> {
  const listRef = doc(
    db,
    Collections.KANBAN.WORKSPACES,
    workspaceId,
    Collections.KANBAN.SPACES,
    spaceId,
    Collections.KANBAN.LISTS,
    listId
  );

  await updateDoc(listRef, {
    taskCount: increment(delta),
    updatedAt: serverTimestamp(),
  });
}

async function updateParentSubtaskCount(parentTaskId: string, delta: number): Promise<void> {
  const parentRef = doc(db, Collections.KANBAN.TASKS, parentTaskId);
  await updateDoc(parentRef, {
    subtaskCount: increment(delta),
    updatedAt: serverTimestamp(),
  });
}

async function updateParentSubtaskCompletedCount(
  parentTaskId: string,
  delta: number
): Promise<void> {
  const parentRef = doc(db, Collections.KANBAN.TASKS, parentTaskId);
  await updateDoc(parentRef, {
    subtaskCompletedCount: increment(delta),
    updatedAt: serverTimestamp(),
  });
}

// ============================================================================
// Goal / OKR Linking Helpers
// ============================================================================

export async function getTasksByGoal(workspaceId: string, goalId: string): Promise<Task[]> {
  const tasksRef = collection(db, Collections.KANBAN.TASKS);
  const q = query(
    tasksRef,
    where("workspaceId", "==", workspaceId),
    where("goalId", "==", goalId),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(taskMap);
}

export async function linkTaskToKeyResult(
  workspaceId: string,
  goalId: string,
  keyResultId: string,
  taskId: string
): Promise<void> {
  const krRef = doc(
    db,
    "kanban_workspaces",
    workspaceId,
    "goals",
    goalId,
    "key_results",
    keyResultId
  );
  const krSnap = await getDoc(krRef);
  if (!krSnap.exists()) return;

  const kr = krSnap.data() as KeyResult;
  const linkedTaskIds = kr.linkedTaskIds || [];
  if (!linkedTaskIds.includes(taskId)) {
    await updateDoc(krRef, {
      linkedTaskIds: [...linkedTaskIds, taskId],
      updatedAt: serverTimestamp(),
    });
  }
}

export async function unlinkTaskFromKeyResult(
  workspaceId: string,
  goalId: string,
  keyResultId: string,
  taskId: string
): Promise<void> {
  const krRef = doc(
    db,
    "kanban_workspaces",
    workspaceId,
    "goals",
    goalId,
    "key_results",
    keyResultId
  );
  const krSnap = await getDoc(krRef);
  if (!krSnap.exists()) return;

  const kr = krSnap.data() as KeyResult;
  const linkedTaskIds = (kr.linkedTaskIds || []).filter((id: string) => id !== taskId);
  await updateDoc(krRef, {
    linkedTaskIds,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Auto-update KR currentValue and goal progress when a linked task changes completion status.
 * Only applies when the KR's unit is "tasks".
 */
async function handleKRAutoProgress(
  workspaceId: string,
  goalId: string,
  keyResultId: string,
  delta: number // +1 for completing, -1 for un-completing
): Promise<void> {
  const krRef = doc(
    db,
    "kanban_workspaces",
    workspaceId,
    "goals",
    goalId,
    "key_results",
    keyResultId
  );
  const krSnap = await getDoc(krRef);
  if (!krSnap.exists()) return;

  const kr = { id: krSnap.id, ...krSnap.data() } as KeyResult;
  if (kr.unit !== "tasks") return;

  const newCurrentValue = Math.max(0, kr.currentValue + delta);
  const newProgress = calculateKeyResultProgress({
    ...kr,
    currentValue: newCurrentValue,
  });

  await updateDoc(krRef, {
    currentValue: newCurrentValue,
    progress: newProgress,
    updatedAt: serverTimestamp(),
  });

  // Recalculate goal progress
  const krCollRef = collection(
    db,
    "kanban_workspaces",
    workspaceId,
    "goals",
    goalId,
    "key_results"
  );
  const allKrSnap = await getDocs(krCollRef);
  const allKrs = allKrSnap.docs.map((d) => {
    const data = d.data() as KeyResult;
    // Use the updated progress for the KR we just changed
    if (d.id === krSnap.id) {
      return { ...data, id: d.id, currentValue: newCurrentValue, progress: newProgress };
    }
    return { ...data, id: d.id };
  });

  const goalProgress = calculateGoalProgress(allKrs);
  const goalRef = doc(db, "kanban_workspaces", workspaceId, "goals", goalId);
  const goalSnap = await getDoc(goalRef);
  if (goalSnap.exists()) {
    const goalData = goalSnap.data();
    const status = getGoalStatusFromProgress(goalProgress, goalData.endDate);
    await updateDoc(goalRef, {
      progress: goalProgress,
      status,
      updatedAt: serverTimestamp(),
    });
  }
}

// ============================================================================
// Bulk Operations
// ============================================================================

export async function reorderTasksInStatus(
  listId: string,
  statusId: string,
  orderedTaskIds: string[]
): Promise<void> {
  const batch = writeBatch(db);

  orderedTaskIds.forEach((taskId, index) => {
    const taskRef = doc(db, Collections.KANBAN.TASKS, taskId);
    batch.update(taskRef, {
      orderInStatus: index,
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
}

export async function bulkUpdateTaskStatus(
  taskIds: string[],
  statusId: string,
  userId: string
): Promise<void> {
  const batch = writeBatch(db);

  for (const taskId of taskIds) {
    const taskRef = doc(db, Collections.KANBAN.TASKS, taskId);
    batch.update(taskRef, {
      statusId,
      updatedAt: serverTimestamp(),
    });
  }

  await batch.commit();

  // Log activity for each task
  for (const taskId of taskIds) {
    await addTaskActivity(taskId, "status_changed", userId, "User", undefined, statusId);
  }
}

// ============================================================================
// Search & Filter Helpers
// ============================================================================

export async function getOverdueTasks(workspaceId: string): Promise<Task[]> {
  const tasksRef = collection(db, Collections.KANBAN.TASKS);
  const now = Timestamp.now();
  const q = query(
    tasksRef,
    where("workspaceId", "==", workspaceId),
    where("dueDate", "<", now),
    where("statusId", "!=", "done")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(taskMap);
}

export async function getTasksDueToday(workspaceId: string): Promise<Task[]> {
  const tasksRef = collection(db, Collections.KANBAN.TASKS);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const q = query(
    tasksRef,
    where("workspaceId", "==", workspaceId),
    where("dueDate", ">=", Timestamp.fromDate(today)),
    where("dueDate", "<", Timestamp.fromDate(tomorrow))
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(taskMap);
}

export async function getMyTasks(workspaceId: string, userId: string): Promise<Task[]> {
  const tasksRef = collection(db, Collections.KANBAN.TASKS);
  // Note: Firestore array-contains doesn't support partial matching
  // We'll fetch all workspace tasks and filter client-side
  const q = query(
    tasksRef,
    where("workspaceId", "==", workspaceId),
    where("statusId", "!=", "done")
  );
  const snapshot = await getDocs(q);
  const tasks = snapshot.docs.map(taskMap);
  return tasks.filter((task) => task.assignees.some((a) => a.userId === userId));
}
